import { useState } from 'react'

import { Box, Icon, useClassNames } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/PopoverMenu'
import { useHover } from 'modules/core/lib/hoverContext'
import i18n from 'modules/core/lib/i18n'

import { usePlanningVersions } from 'modules/time_planning/store/collections/planningVersions'
import type PlanningVersion from 'modules/time_planning/store/models/PlanningVersion'

import DeleteModal from './DeleteModal'
import EditModal from './EditModal'
import './index.styles.css'

const stylesRefs = {
  actions: 'Menu_actions',
  labels: 'Menu_labels',
  future: 'Menu_future',
}

type Status = 'editing' | 'deleting' | 'normal'
interface ContentProps {
  employeeId: number
  planningVersion: PlanningVersion
  actionOptions: PopoverOption[]
  canEditPlanningVersions: boolean
}
interface Props {
  employeeId: number
  canEditPlanningVersions: boolean
  planningVersion: PlanningVersion
  onClickDelete: () => void
}

interface PopoverOption {
  title: string
  onClick: () => void
}

const Content = ({
  employeeId,
  actionOptions,
  planningVersion,
  canEditPlanningVersions,
}: ContentProps) => {
  const hover = useHover()
  const [isOpened, setIsOpened] = useState(false)
  const planningVersions = usePlanningVersions()
  const cx = useClassNames(stylesRefs)
  const current = planningVersions.isCurrent(planningVersion, employeeId)
  const future = planningVersion.future
  const actionStyle = cx('actions', { future })
  const labelStyle = cx('labels', { future })
  const hasActions = actionOptions.length > 0

  if (isOpened || (hover && hasActions && canEditPlanningVersions)) {
    return (
      <Popover type="list" onClick={setIsOpened}>
        <Popover.Trigger>
          <Box className={actionStyle} onClick={(e: MouseEvent) => e.preventDefault()}>
            <Icon.Medium icon="ellipsis" color="grey000White" />
          </Box>
        </Popover.Trigger>
        <Popover.Content side="bottom" align="end">
          {actionOptions.map((option) => (
            <Popover.TouchableOption
              key={option.title}
              onClick={option.onClick}
              title={option.title}
            />
          ))}
        </Popover.Content>
      </Popover>
    )
  }

  if (future) {
    return (
      <Box className={labelStyle}>{i18n.t('time_planning.planning_versions.navigator.future')}</Box>
    )
  }

  if (current) {
    return (
      <Box className={labelStyle}>
        {i18n.t('time_planning.planning_versions.navigator.current')}
      </Box>
    )
  }

  return null
}

const Menu = ({ planningVersion, onClickDelete, employeeId, canEditPlanningVersions }: Props) => {
  const [status, setStatus] = useState<Status>('normal')
  const isEditing = (): boolean => status === 'editing'
  const isDeleting = (): boolean => status === 'deleting'
  const onClose = () => setStatus('normal')

  const actionOptions: PopoverOption[] = [
    {
      onClick: () => {
        setStatus('editing')
      },
      title: i18n.t('time_planning.planning_versions.actions.change_date'),
    },
    {
      onClick: () => {
        setStatus('deleting')
      },
      title: i18n.t('time_planning.planning_versions.actions.delete'),
    },
  ]

  return (
    <Box className={'Menu_menu'}>
      <Content
        employeeId={employeeId}
        actionOptions={planningVersion.isDefault ? [] : actionOptions}
        planningVersion={planningVersion}
        canEditPlanningVersions={canEditPlanningVersions}
      />
      {isEditing() && <EditModal planningVersion={planningVersion} onClose={onClose} />}
      {isDeleting() && <DeleteModal onClose={onClose} onSubmit={onClickDelete} />}
    </Box>
  )
}

export default observer(Menu)
